import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import {DefaultRootState} from 'react-redux';

import {AuthState, authTypeSelector} from 'app/auth/slice';
import {showUpgradeModal, UPGRADE_MODALS} from 'app/toasts/slice';
import {FAKE_TEST_DOMAIN} from 'app/utils/constants';
import {getCSRFToken} from 'app/utils/urls';

import {captureCookieAuthError, captureTokenAuthError} from './auth-errors';

const baseQuery = fetchBaseQuery({
  baseUrl:
    // prepend the fake-test-domain during tests
    process.env.NODE_ENV == 'test' ? FAKE_TEST_DOMAIN : '',

  fetchFn: sweetFetchFn,

  prepareHeaders: (headers, api) => {
    const auth = (api.getState() as DefaultRootState).auth;

    if (auth.type === 'token') {
      headers.set('Authorization', `Bearer ${auth.token}`);
    }

    return headers;
  },
});

export const apiQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const authType = authTypeSelector(api.getState() as DefaultRootState).type;
  args = addCredentials(args, authType);

  let result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    if (authType === 'token') {
      captureTokenAuthError(result.error, api, [
        'invalid-token',
        'inactive-user',
      ]);
    }
    if (authType === 'session-cookie') {
      captureCookieAuthError(result.error, api);
    }
    const isSudoRequired = Object.values(result.error.data as any)
      .flat()
      .includes(UPGRADE_MODALS.SUDO_REQUIRED);
    if (isSudoRequired) {
      api.dispatch(showUpgradeModal(UPGRADE_MODALS.SUDO_REQUIRED));
    }
  }

  return result;
};

// low level adjustments when making a request
function sweetFetchFn(
  originalInput: RequestInfo,
  init?: RequestInit | undefined
): Promise<Response> {
  // ensures input is a Request instance, not a string
  const input = new Request(originalInput);

  // django need this to request.is_ajax method works
  input.headers.set('X-Requested-With', 'XMLHttpRequest');

  // if we are making changes, add Django CSRF token
  if (input.method !== 'GET') {
    input.headers.append('X-CSRFToken', getCSRFToken() || '');
  }

  // for compat with older IT systems we disguise the PATCH as POST
  if (input.method === 'PATCH') {
    input.headers.append('X-HTTP-Method-Override', 'PATCH');

    init = {
      ...init,
      method: 'POST',
    };
  }

  return fetch(input, init);
}

/**
 * Adds credentials="omit" to the fetch request if the authentication is made via a token
 * So no cookies are sent and cookie based sessions are ignored
 *
 * @param {string | FetchArgs} args
 * Fetch arguments
 *
 * @param {AuthState['type']} authType
 * The authentication type
 */
function addCredentials(
  args: string | FetchArgs,
  authType: AuthState['type']
): string | FetchArgs {
  if (authType !== 'token') {
    return args;
  }

  const newArgs: FetchArgs = typeof args == 'string' ? {url: args} : args;
  newArgs.credentials = 'omit';
  return newArgs;
}
